"use client";

import React, {type ForwardedRef, useEffect, useState, useRef} from "react";
import Tippy from "@tippyjs/react/headless";
import "tippy.js/dist/tippy.css";

import {cn, useMergeRefs} from "@isudio/utils";

import {Label, useTippyRegistry} from "../";

type InputProps =
  React.InputHTMLAttributes<HTMLInputElement>
  & {
  mask?: RegExp;
  format?: "properCase" | "email" | "phone" | "currency" | "number";
  countryCode?: string;
  error?: string;
  display?: true | false;
  roColor?: string;
  labelClass?: string;
  buttonIcon?: string;
  onButtonClick?: () => void;
  showToast?: (message: string) => void;
};

const InputValidator = React.forwardRef<HTMLInputElement, InputProps>(
  ({
     className,
     type = "text",
     minLength,
     maxLength,
     mask,
     format,
     countryCode,
     error,
     display = true,
     roColor = "text-custom-gray-300",
     labelClass = "text-generic-dull",
     onChange,
     onBlur,
     buttonIcon,
     onButtonClick,
     showToast,
     ...props
   },
   ref: ForwardedRef<HTMLInputElement>
  ) => {
    const {
      register,
      unregister
    } = useTippyRegistry();

    const internalRef = useRef<HTMLInputElement | null>(null);
    const initialVal = props.value ?? props.defaultValue ?? (internalRef.current?.value ?? "");
    const [hasValue, setHasValue] = useState(Boolean(String(initialVal).trim()));
    const isPassword = type === "password";
    const [showPassword, setShowPassword] = useState(!isPassword);
    const [showError, setShowError] = useState(false);
    const [hovering, setHovering] = useState(false);
    const hideTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearHideTimer = () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };

    const startHideTimer = (delay = 2000) => {
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => {
        setShowError(false);
        hideTimerRef.current = null;
      }, delay);
    };

    useEffect(() => {
      const domVal = internalRef.current?.value;
      const v = props.value ?? props.defaultValue ?? domVal ?? "";
      setHasValue(Boolean(String(v).trim()));
    }, [props.value, props.defaultValue]);

    useEffect(() => {
      if (error && !hovering) {
        setShowError(true);
        startHideTimer();
      } else if (!error) {
        setShowError(false);
        clearHideTimer();
      }
      return clearHideTimer;
    }, [error]);

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
      if (error) {
        setShowError(true);
        startHideTimer();
      }
    };

    const handleMouseEnter = () => {
      if (!error) return;
      clearHideTimer();
      setHovering(true);
      setShowError(true);
    };

    const handleMouseLeave = () => {
      setHovering(false);
      setShowError(false);
      clearHideTimer();
    };

    const handleBeforeInput = (e: any) => {
      if (props.readOnly) return;

      if (mask) {
        if (!mask.test(e.data)) {
          e.preventDefault();
          showToast?.("Invalid characters are blocked from input!");
        }
      }

      if (format === "phone") {
        if (e.target.value.length === 0) {
          e.preventDefault();
          const target = e.target as HTMLInputElement;
          target.value = `+${countryCode} ${e.data}`;
          onChange?.({ target } as any);
        }
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let text = e.target.value;

      switch (format) {
        case "properCase":
          text = text.replace(/\b\w+/g, function (word) {
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          });
          if (text !== e.target.value) {
            (e.target as any).value = text;
          }
          break;

        case "email":
          (e.target as any).value = text.toLowerCase();
          break;

        case "phone":
          if (countryCode) {
            const countryCodePrefix = `+${countryCode} `
            // If somehow the country code got removed, restore it
            if (!text.startsWith(countryCodePrefix)) {
              // Extract just the number part if any
              const numberPart = text.replace(/^\+\d+\s*/, "")
              text = countryCodePrefix + numberPart
              ;(e.target as any).value = text
            }

            if (text.length > countryCodePrefix.length) {
              const numberPart = text.substring(countryCodePrefix.length).replace(/\D/g, "")
              let formattedNumber = ""

              if (numberPart.length > 0) {
                if (numberPart.length <= 3) {
                  formattedNumber = numberPart
                } else if (numberPart.length <= 6) {
                  formattedNumber = `${numberPart.slice(0, 3)} ${numberPart.slice(3)}`
                } else {
                  formattedNumber = `${numberPart.slice(0, 3)} ${numberPart.slice(3, 6)} ${numberPart.slice(6, 10)}`
                }
              }

              text = countryCodePrefix + formattedNumber;
              (e.target as any).value = text;
            }
          }
          break;

      }

      setHasValue(Boolean(String(text).trim()));
      onChange?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (props.readOnly) return;

      if (format === "phone" && countryCode) {
        const target = e.target as HTMLInputElement
        const countryCodePrefix = `+${countryCode} `

        // Check if backspace or delete key is pressed
        if (e.key === "Backspace" || e.key === "Delete") {
          const { selectionStart, selectionEnd, value } = target

          if (selectionStart !== null && selectionEnd !== null) {
            // If selection includes part of the country code, adjust selection
            if (selectionStart < countryCodePrefix.length) {
              e.preventDefault()

              // If selection extends beyond country code, delete only the number part
              if (selectionEnd > countryCodePrefix.length) {
                target.value = value.substring(0, countryCodePrefix.length) + value.substring(selectionEnd);
                target.setSelectionRange(countryCodePrefix.length, countryCodePrefix.length)

                // Trigger change event
                const changeEvent = new Event("input", { bubbles: true })
                target.dispatchEvent(changeEvent)
              }
              return
            }

            // For backspace: prevent if cursor is at the end of country code
            if (
              e.key === "Backspace" &&
              selectionStart === countryCodePrefix.length &&
              selectionEnd === selectionStart
            ) {
              e.preventDefault();
              if (onButtonClick) onButtonClick();
              return;
            }

            // For delete: only prevent if there's nothing to delete after country code
            if (e.key === "Delete" && selectionStart === countryCodePrefix.length && selectionEnd === selectionStart) {
              // Allow deletion if there are characters after the country code
              if (selectionStart >= value.length) {
                e.preventDefault();
                if (onButtonClick) onButtonClick();
                return;
              }
              // Let the default delete behavior happen for characters after country code
            }
          }
        }
      }

      // Call original onKeyDown if provided
      props.onKeyDown?.(e)
    }

    const inputEl = (
      <div
        className="relative w-full flex gap-2 justify-center items-center">
        {buttonIcon ?
          <button
            className="absolute focus:outline-none left-3.5"
            type="button"
            tabIndex={-1}
            onClick={onButtonClick ? onButtonClick : undefined}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <i
              className={`${buttonIcon} text-sm cursor-pointer transition-colors`}
            />
          </button>
          : null
        }

        <input
          type={isPassword && !showPassword ? "password" : "text"}
          ref={useMergeRefs(internalRef, ref, (props as any).ref)}
          className={cn(
            "flex h-9 w-full border-input-field bg-transparent px-2 py-1 text-base md:text-sm tracking-tight focus-visible:outline-none focus-visible:ring-0 placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
            buttonIcon ? "pl-10" : "",
            type === "password" ? "pr-10" : "",
            props.readOnly ? roColor : "",
            error ? "border-input-field__error" : "",
            display ? "" : "hidden",
            className
          )}
          placeholder={props.placeholder}
          minLength={minLength}
          maxLength={maxLength}
          spellCheck="false"
          onBeforeInput={handleBeforeInput}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onBlur={handleBlur}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          {...props}
        />
        {isPassword && (
          <button
            className="absolute focus:outline-none right-3.5"
            type="button"
            tabIndex={-1}
            onClick={() => setShowPassword(!showPassword)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <i
              className={`fa-duotone fa-light ${isPassword && !showPassword ? "fa-eye" : "fa-eye-slash"} text-sm text-generic-dull hover:text-gray-500 dark:hover:text-white cursor-pointer transition-colors`}
            />
          </button>
        )}
        {display && hasValue && (
          <Label className={cn(
            `absolute -top-2 left-2.25 text-[10px] font-semibold`,
            labelClass
          )}>
            {props.placeholder}
          </Label>
        )}
      </div>
    );

    return (
      <Tippy
        onCreate={instance => register(instance)}
        onDestroy={instance => unregister(instance)}
        className={cn(
          "err-tooltip",
          display ? "" : "hidden",
        )}
        placement="bottom"
        visible={!!error && showError}
        interactive={false}
        hideOnClick={true}
        delay={0}
        render={(attrs) => (
          <div
            {...attrs}
            tabIndex={-1}
            className={cn(
              "err-tooltip-box box bg-[#640a0a] text-white text-sm px-2 py-1 rounded shadow whitespace-pre-line",
              display ? "" : "hidden",
            )}
          >
            {error ?? ""}
            <div
              data-popper-arrow
              className="err-tooltip-arrow bg-[#640a0a]! w-3! h-3! rotate-45!"
            />
          </div>
        )}
      >
        {inputEl}
      </Tippy>
    );
  }
);

InputValidator.displayName = "InputValidator";
export {InputValidator};

